import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { PostCard, PostDetail, SubNav } from '@/types'

import { ArrowLeft, BookOpen, ChevronRight } from 'lucide-react'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Container } from '@/components/generals/container'
import { ArticleBody, ArticleWidget, splitLeadingWidget } from '@/components/content/article-body'
import { EditorialByline } from '@/components/content/editorial-byline'
import { FaqSection } from '@/components/content/faq-section'
import { MedicalDisclaimer } from '@/components/content/medical-disclaimer'
import { PillarChip } from '@/components/content/pillar-chip'
import { PostCard as PostCardItem } from '@/components/content/post-card'
import { TldrBox } from '@/components/content/tldr-box'

import {
    articleSchema,
    breadcrumbSchema,
    faqSchema,
    howToSchema
} from '@/lib/structured-data'

import {
    getPostBySlug,
    getPublishedParams,
    getRelatedPosts,
    getSubNav,
    isKnownSection
} from '@/lib/queries'

import { brand } from '@/constants/brand'

interface ArticlePageProps {
    post: PostDetail
    related: PostCard[]
    subNav: SubNav | null
}

const ArticlePage: FC<ArticlePageProps> = ({ post, related, subNav }): ReactNode => {
    const pillarHref = `/${post.pillarSlug}`

    // Tool pages lead with their widget, rendered straight under the H1 so the
    // tool is the first thing a visitor sees. The cover image is kept for cards
    // and social previews only, and the rest of the body renders as usual.
    const { widget: leadWidget, rest: bodyContent } = splitLeadingWidget(post.body)
    const leadsWithWidget = leadWidget !== null

    const breadcrumb = breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: post.pillarName, path: pillarHref },
        { name: post.title, path: `/${post.pillarSlug}/${post.slug}` }
    ])

    const graphs = [articleSchema(post), breadcrumb]
    if (post.faqs.length > 0) graphs.push(faqSchema(post.faqs))

    const howTo = howToSchema(post)
    if (howTo) graphs.push(howTo)

    return (
        <Layout subNav={subNav}>
            <Seo
                title={post.title}
                description={post.excerpt}
                canonical={`/${post.pillarSlug}/${post.slug}`}
                ogImage={post.coverImage ?? undefined}
                ogImageAlt={post.title}
                type='article'
                keywords={post.tags}
                tags={post.tags}
                section={post.pillarName}
                author={brand.name}
                publishedTime={post.publishedAt}
                modifiedTime={post.updatedAt}
                jsonLd={graphs}
            />

            <article>
                <Container size='narrow' className='pt-12 sm:pt-16'>
                    <nav className='mb-6 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
                        <Link href='/' className='transition-colors hover:text-foreground'>
                            Home
                        </Link>
                        <ChevronRight className='size-3.5' />
                        <Link
                            href={pillarHref}
                            data-mdlfy-cms={`pillars:${post.pillarId}:name`}
                            className='transition-colors hover:text-foreground'
                        >
                            {post.pillarName}
                        </Link>
                    </nav>

                    <PillarChip
                        accent={post.accent}
                        label={post.pillarName}
                        href={pillarHref}
                        cmsRef={`pillars:${post.pillarId}:name`}
                    />

                    <h1
                        data-mdlfy-cms={`posts:${post.id}:title`}
                        className='mt-5 font-heading text-4xl font-semibold leading-[1.1] tracking-tight text-foreground sm:text-5xl'
                    >
                        {post.title}
                    </h1>

                    {leadWidget && <ArticleWidget name={leadWidget} />}

                    <p
                        data-mdlfy-cms={`posts:${post.id}:excerpt`}
                        className='mt-5 text-xl leading-relaxed text-muted-foreground'
                    >
                        {post.excerpt}
                    </p>

                    <EditorialByline
                        className='mt-8'
                        updatedAt={post.updatedAt}
                        publishedAt={post.publishedAt}
                        readMinutes={post.readMinutes}
                    />

                    {/*
                        Parent-pillar link sits above the body on purpose: the
                        internal-linking rules require it inside the first ~150
                        words, with descriptive anchor text.
                    */}
                    <p className='mt-8 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground'>
                        <BookOpen className='size-4 shrink-0 text-primary' />
                        <span>
                            Part of{' '}
                            <Link
                                href={pillarHref}
                                data-mdlfy-cms={`pillars:${post.pillarId}:h1`}
                                className='font-medium text-foreground underline underline-offset-2 hover:text-primary'
                            >
                                {post.pillar.h1}
                            </Link>
                        </span>
                    </p>
                </Container>

                {post.coverImage && !leadsWithWidget && (
                    <Container className='mt-10'>
                        <div className='relative aspect-[16/9] overflow-hidden rounded-2xl bg-muted'>
                            <img
                                src={post.coverImage}
                                alt={post.title}
                                className='absolute inset-0 size-full object-cover'
                            />
                        </div>
                    </Container>
                )}

                <Container size='narrow' className='mt-12'>
                    {post.tldr && <TldrBox content={post.tldr} className='mb-10' />}

                    <ArticleBody content={bodyContent} />

                    {post.tags.length > 0 && (
                        <div className='mt-10 flex flex-wrap gap-2 border-t border-border pt-8'>
                            {post.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className='rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground'
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}

                    <FaqSection faqs={post.faqs} table='post_faqs' className='mt-12' />

                    <MedicalDisclaimer className='mt-12' />

                    <div className='mt-10'>
                        <Link
                            href={pillarHref}
                            className='inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline'
                        >
                            <ArrowLeft className='size-4' />
                            <span>
                                Back to <span data-mdlfy-cms={`pillars:${post.pillarId}:h1`}>{post.pillar.h1}</span>
                            </span>
                        </Link>
                    </div>
                </Container>
            </article>

            {related.length > 0 && (
                <section className='mt-20 border-t border-border bg-secondary/40'>
                    <Container className='py-16'>
                        <h2 className='font-heading text-2xl font-semibold tracking-tight text-foreground'>
                            More from <span data-mdlfy-cms={`pillars:${post.pillarId}:name`}>{post.pillarName}</span>
                        </h2>
                        <div className='mt-10 grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
                            {related.map((item) => (
                                <PostCardItem key={item.id} post={item} />
                            ))}
                        </div>
                    </Container>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const params = await getPublishedParams()
    return {
        paths: params.map((item) => ({ params: { section: item.pillar, slug: item.slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const section = typeof params?.section === 'string' ? params.section : ''
    const post = await getPostBySlug(slug)

    if (!post) {
        return { notFound: true, revalidate: 60 }
    }

    // The article lives under a different section than the one requested. This
    // is how the old /<nav-group>/<slug> URLs keep working after articles were
    // re-homed under their pillar, and it keeps working for any future move,
    // no redirect table to maintain. Restricted to real sections so we do not
    // create an unbounded set of redirecting URLs.
    if (post.pillarSlug !== section) {
        if (await isKnownSection(section)) {
            return {
                redirect: {
                    destination: `/${post.pillarSlug}/${post.slug}`,
                    permanent: true
                }
            }
        }

        return { notFound: true, revalidate: 60 }
    }

    const [related, subNav] = await Promise.all([
        getRelatedPosts(post.pillar.id, post.id, post.clusterGroup, 3),
        getSubNav(post.pillar.navGroupSlug)
    ])

    return {
        props: { post, related, subNav },
        revalidate: 300
    }
}

export default ArticlePage
